import React from 'react';
import './DocsModal.css';
import { X, Bot, Search, Shield, FileCode, AlertCircle, Layers, Lock } from 'lucide-react';

function DocsModal({ isOpen, onClose }) {
    if (!isOpen) return null;

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content" onClick={e => e.stopPropagation()}>
                <div className="modal-header">
                    <h2>MetaProbe Documentation</h2>
                    <button className="modal-close" onClick={onClose}>
                        <X size={20} />
                    </button>
                </div>

                <div className="modal-body">
                    <section className="doc-section">
                        <h3><Bot size={18} /> What is MetaProbe?</h3>
                        <p>
                            MetaProbe analyzes images and videos to detect AI-generated content by examining
                            embedded metadata, binary patterns, and industry-standard markers. The tool shows
                            you the actual file metadata separately from our AI analysis.
                        </p>
                    </section>

                    <section className="doc-section">
                        <h3><Lock size={18} /> Your Files Stay Local</h3>
                        <p>
                            MetaProbe runs entirely in your browser. Files you drop in are never
                            uploaded to any server — all metadata parsing, C2PA verification, and
                            AI detection happens on your own machine.
                        </p>
                    </section>

                    <section className="doc-section">
                        <h3><Search size={18} /> Detection Methods</h3>
                        <ul className="doc-list">
                            <li>
                                <strong>C2PA Content Credentials:</strong> Cryptographically signed AI markers
                                used by Sora, Veo, Adobe, and other major tools. Highest confidence.
                            </li>
                            <li>
                                <strong>IPTC Digital Source:</strong> Industry-standard metadata field indicating
                                AI generation. High confidence.
                            </li>
                            <li>
                                <strong>Software Signatures:</strong> Checks encoder, creator, and software fields
                                for known AI tools (Midjourney, DALL-E, Stable Diffusion, Runway, etc.)
                            </li>
                            <li>
                                <strong>Embedded Prompts:</strong> Scans binary data for AI generation prompts
                                and parameters hidden in file structure.
                            </li>
                            <li>
                                <strong>Filename Patterns:</strong> Recognizes common AI generator naming patterns
                                (e.g., Midjourney's username_prompt_UUID format). Lower confidence.
                            </li>
                            <li>
                                <strong>24fps Heuristic:</strong> Many AI video generators output exactly 24.000 fps.
                                Very low confidence - only adds to existing detection.
                            </li>
                        </ul>
                    </section>

                    <section className="doc-section">
                        <h3><Shield size={18} /> Confidence Levels</h3>
                        <div className="confidence-guide">
                            <div className="conf-item">
                                <span className="conf-badge very-high">90-100%</span>
                                <span>Very High - C2PA verified or explicit AI markers in metadata</span>
                            </div>
                            <div className="conf-item">
                                <span className="conf-badge high">70-89%</span>
                                <span>High - Software signatures or embedded prompts found</span>
                            </div>
                            <div className="conf-item">
                                <span className="conf-badge medium">50-69%</span>
                                <span>Medium - Filename patterns + supporting evidence</span>
                            </div>
                            <div className="conf-item">
                                <span className="conf-badge low">1-49%</span>
                                <span>Low - Heuristics only (filename or framerate)</span>
                            </div>
                        </div>
                    </section>

                    <section className="doc-section">
                        <h3><Layers size={18} /> Understanding the Panels</h3>
                        <ul className="doc-list">
                            <li>
                                <strong>Preview:</strong> Shows the uploaded file. Drag a new file here to replace.
                            </li>
                            <li>
                                <strong>AI Detection:</strong> Our analysis of whether the file is AI-generated,
                                including confidence level, generator name, and exactly what triggered the detection.
                            </li>
                            <li>
                                <strong>Metadata Tree:</strong> The actual metadata embedded in the file -
                                nothing added by us. Fields that match AI signatures are highlighted.
                            </li>
                            <li>
                                <strong>Raw JSON:</strong> Complete file metadata in JSON format for technical analysis.
                            </li>
                        </ul>
                    </section>

                    <section className="doc-section">
                        <h3><AlertCircle size={18} /> Important Limitations</h3>
                        <p>
                            <strong>Many AI generators strip metadata.</strong> Midjourney, Kling, and others
                            don't embed identifying information. If a file has been re-saved or edited,
                            markers may be removed. A "No AI Detected" result does NOT guarantee
                            the content is human-made.
                        </p>
                    </section>

                    <section className="doc-section">
                        <h3><FileCode size={18} /> Supported Formats</h3>
                        <p>
                            <strong>Images:</strong> PNG, JPEG, WebP, TIFF, GIF<br />
                            <strong>Videos:</strong> MP4, MOV, WebM, AVI, MKV
                        </p>
                    </section>
                </div>
            </div>
        </div>
    );
}

export default DocsModal;
